import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Generic hook for GET requests with loading/error state.
 */
export function useApiGet(endpoint, params = null, immediate = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (overrideParams) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(endpoint, { params: overrideParams || params });
      setData(res.data);
      return res.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'An error occurred';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint, params]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, [immediate, fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Pre-built API functions
 */
export function useSettings() {
  const result = useApiGet('/settings');
  // Transform [{key, value}] array into {key: value} object
  const settings = React.useMemo(() => {
    const raw = result.data;
    if (!raw) return null;
    if (Array.isArray(raw)) {
      return raw.reduce((acc, item) => { acc[item.key] = item.value; return acc; }, {});
    }
    if (raw.data && Array.isArray(raw.data)) {
      return raw.data.reduce((acc, item) => { acc[item.key] = item.value; return acc; }, {});
    }
    return raw;
  }, [result.data]);
  return { ...result, data: settings };
}

export function usePortfolios(params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchPortfolios = useCallback(async (fetchParams) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/portfolio', { params: fetchParams });
      const result = res.data;
      // Support both paginated and array responses
      if (Array.isArray(result)) {
        setData(result);
        setHasMore(false);
      } else if (result.data) {
        setData(result.data);
        setHasMore(result.page < result.totalPages);
      } else {
        setData(result);
        setHasMore(false);
      }
      return result;
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load portfolios';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolios(params);
    // eslint-disable-next-line
  }, []);

  return { data, loading, error, hasMore, refetch: fetchPortfolios };
}

export function useServices() {
  return useApiGet('/services');
}

export function useServiceDetail(slug) {
  return useApiGet(slug ? `/services/detail/${slug}` : null, null, !!slug);
}

export function useClients() {
  return useApiGet('/clients');
}

export function usePortfolioById(id) {
  return useApiGet(`/portfolio/${id}`);
}

/**
 * Submit contact form (POST)
 */
export function useContactForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submitContact = async (formData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await api.post('/contact', formData);
      setSuccess(true);
      return true;
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to send message';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError(null);
    setSuccess(false);
  };

  return { submitContact, loading, error, success, reset };
}

export default api;
